/**
 * audience.ts · 09_APP · OS-006
 *
 * HARD RULE 3, on the client side.
 *
 * WHY THIS EXISTS TWICE
 * The Python gateway (10_GATEWAY/verbs.py) has the same guard, because an
 * agent calls it. This app does not go through that gateway: it talks to
 * PostgREST directly with the signed-in user's token, so RLS and
 * `security invoker` do the enforcing and there is no server anywhere holding
 * a service key. That is the right architecture and it is why the guard has
 * to exist on both paths.
 *
 * TWO IMPLEMENTATIONS OF ONE RULE IS A DRIFT RISK, and drift in this
 * particular rule reaches 1,172 real women. So:
 *
 *   · The NUMBERS have one owner, 10_GATEWAY/audience_baselines.json, which
 *     Python reads directly. The values below are a mirror of it, and
 *     audience.test.ts loads that file and asserts they are identical. Change
 *     one without the other and the suite goes red.
 *   · The BEHAVIOUR is asserted against the same fixtures on both sides.
 *     audience.test.ts mirrors test_contract.py case for case.
 *
 * That does not make drift impossible. It makes it fail a test.
 *
 * WHY MIRRORED RATHER THAN IMPORTED, and why it stays that way even though the
 * original reason is gone. The first version imported the JSON across the
 * project root. It typechecked and built, and then the dev server returned 403
 * for its own index.html: Vite's fs.allow list could not match a path
 * containing " : ", and the project lived in `C2C Email : CRM`. Mirroring four
 * numbers and pinning them with a test was cheaper than switching off
 * fs.strict, which is a real control on the dev server.
 *
 * ✅ 2026-08-03: Emily renamed the folder network wide, so the colon is gone,
 * fs.strict is back ON and a cross-root import would work now. This stays
 * mirrored anyway, and that is a choice rather than inertia: the test reads the
 * real file and fails on any difference, so the mirror cannot drift, and the
 * numbers are four integers that a reader can see at the top of this file
 * without following an import. New shared config does NOT need to copy this
 * pattern. 11_EDGE/form_registry.json is imported directly, because a form
 * registry is too large to mirror honestly.
 */

export const CHANNEL_RECIPIENTS: Record<string, number> = {
  newsletter: 1185,
  appeals: 1169,
  events: 1169,
  media_release: 0,
};
export const SUPPRESSED_TOTAL = 181;
export const VERIFIED_ON = "2026-10-20";

/** The shape os_audience returns. */
export interface AudiencePayload {
  channel: string;
  is_mailing_channel: boolean | null;
  recipients: number;
  suppressed_total?: number;
  rule?: string;
  source?: string;
  checked_at?: string;
}

export interface AudienceVerdict {
  /** Refused outright. Never show a count. */
  refused: boolean;
  reason?: string;
  /** Present when allowed. */
  payload?: AudiencePayload;
  /** Non-blocking, but must be shown next to the number, never collapsed. */
  warnings: string[];
}

export function guardAudience(result: AudiencePayload | null): AudienceVerdict {
  if (!result || typeof result !== "object") {
    return {
      refused: true,
      reason:
        "os_audience did not return an audience. Refusing to treat an " +
        "unrecognised answer as one.",
      warnings: [],
    };
  }

  const { channel, is_mailing_channel: isMailing, recipients } = result;

  if (isMailing === null || isMailing === undefined) {
    return {
      refused: true,
      reason: `“${channel}” is not a known consent channel, so there is no such audience.`,
      warnings: [],
    };
  }

  if (isMailing !== true) {
    return {
      refused: true,
      reason:
        `“${channel}” is not a mailing channel. Consent to share with a ` +
        "partner is a DISCLOSURE. It is not permission to email, and turning " +
        "one into the other is the failure is_mailing_channel exists to " +
        "prevent. Mailing channels: newsletter, appeals, events, media_release.",
      warnings: [],
    };
  }

  if (!Number.isInteger(recipients) || recipients < 0) {
    return {
      refused: true,
      reason:
        `os_audience returned a recipient count that is not a whole number ` +
        `(${String(recipients)}). Refusing to show that as an audience size.`,
      warnings: [],
    };
  }

  const warnings: string[] = [];
  const expected = CHANNEL_RECIPIENTS[channel];

  if (expected !== undefined) {
    if (recipients !== expected) {
      const tripled = expected > 0 && recipients === expected * 3;
      warnings.push(
        `The ${channel} audience has moved: ${recipients} now, ${expected} ` +
          `when last verified on ${VERIFIED_ON}. That is not automatically ` +
          `wrong, but somebody has to look.` +
          (tripled
            ? ` Note ${recipients} is exactly 3 × ${expected}, which is the ` +
              "row-count trap: mailable holds one row per person per channel."
            : "")
      );
    }
  } else {
    // The gap that allowed the appeals hole: a mailing channel with no
    // recorded baseline was silently trusted.
    warnings.push(
      `No baseline recorded for “${channel}”, so ${recipients} has not been ` +
        "checked against anything. Verify it before it is used as a send " +
        "list, and add it to audience_baselines.json."
    );
  }

  const suppressed = result.suppressed_total;
  if (typeof suppressed === "number" && suppressed !== SUPPRESSED_TOTAL) {
    warnings.push(
      `Suppressions have moved: ${suppressed} now, ${SUPPRESSED_TOTAL} when ` +
        "last verified. A suppression can only be lifted by the person, so a " +
        "fall here is worth stopping for."
    );
  }

  return { refused: false, payload: result, warnings };
}
